import { useCallback, useEffect, useRef, useState } from "react";
import ICONS from "../../../../../assets/Icons";
import { useDispatch, useSelector } from "react-redux";
import { AccountsPerDayThunk } from "../../../../../redux/features/Settings/Statistics";
import { toCamelCase } from "./../../../../../utils/toCamelCase";
import {
  errorcode,
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";
import dayjs from "dayjs";
import CommonToast from "../../../../_utils/CommonToast";
import globalText from "../../../../../utils/globalText";
import GraphHeader from "../../CommonComponents/GraphHeader";
import FecthingPage from "../../CommonComponents/FecthingPage";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";

/**
 * AccountsPerDay - A component that displays accounts per day trends over time
 * using a line graph. Supports filtering on the basis of 7 days or 28 days. or beneficiary
 * or internal.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.flow - Display flow: 'homeDashboard' or other
 * @param {boolean} [props.autoFetch=false] - Whether to fetch data automatically on mount
 * @param {Function} props.toggleAddedToHome - Callback to toggle widget on home dashboard
 * @param {boolean} props.isAddedToHome - Whether this widget is added to home dashboard
 * @returns {JSX.Element} Rendered accounts per day graph component
 */

const DEFAULT_GRAPH_DATA = {
  // NOTE: This is default dummy data for graph - used as fallback before fetch
  defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
  dataFirst: [50, 50, 80, 70, 30, 20, 100],
  xAxisData: [
    "2025-01-01",
    "2025-01-02",
    "2025-01-03",
    "2025-01-04",
    "2025-01-05",
    "2025-01-06",
    "2025-01-07",
  ],
  total_accounts: 7,
  // account,
};

const AccountsPerDay = ({
  flow,
  autoFetch = false,
  toggleAddedToHome,
  isAddedToHome,
}) => {
  /**
   * Default fallback data for line graph - used as initial state before API fetch.
   * @type {Object}
   * @property {number[]} defaultItemsSizeForXAxis - Array of indices for x-axis positions
   * @property {number[]} dataFirst - Credit amount line data
   * @property {string[]} xAxisData - Date labels for x-axis
   * @property {number[]} total_accounts - total number of accounts
   */
  const dispatch = useDispatch();
  const toastShownRef = useRef(false);

  const homeDashboard = toCamelCase(globalText?.HOME_DASHBOARD);
  const accountTypeOption = toCamelCase(globalText?.ACCOUNT_TYPE_OPTION);
  const fieldForce = toCamelCase(globalText?.FIELD_FORCE);

  const ACCOUNT_TYPE_MAPPING = {
    [globalText?.BENEFICIARY_OPTION.toLowerCase()]:
      globalText?.BENEFICIARY?.toLowerCase(),
    [fieldForce]: "field_force",
    [globalText?.REIMBURSEMENT.toLowerCase()]:
      globalText?.REIMBURSEMENT.toLowerCase(),
    [globalText?.INTERNAL.toLowerCase()]: globalText?.INTERNAL.toLowerCase(),
  };

  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);
  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );
  const [selectedAccountTypeOption, setSelectedAccountTypeOption] = useState(
    globalText?.BENEFICIARY_OPTION.toLowerCase(), //"bene"
  );
  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);

  const services = useSelector((state) => state.refreshToken.services);
  const globalStatisticsService = services?.includes(
    globalText?.VIEW_STATISTICS,
  ); // Global service for statistics. "VStatistics"

  // Select loading and error states from Redux Thunk
  const loginData = useSelector((state) => state.refreshToken.data);
  const { AccountsPerDayLoading, AccountsPerDayError } = useSelector(
    (state) => state.settingStatistics,
  );
  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  const transformReportData = (reports = []) => {
    return reports.reduce(
      (acc, item, index) => {
        acc.defaultGraphDataItemsForXaxis.push(index);
        acc.graphDataFirst.push(item.count);
        acc.xAxisLabels.push(item.date);

        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        xAxisLabels: [],
      },
    );
  };

  /* Fetch and update state with API data */
  const handleCalculation = useCallback(
    async (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        const { defaultGraphDataItemsForXaxis, graphDataFirst, xAxisLabels } =
          transformReportData(reportData.generate_reports || []);

        // Update graph data with transformed arrays and summary totals
        setGraphData({
          defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
          dataFirst: graphDataFirst,
          xAxisData: xAxisLabels,
          totalAccounts: reportData.total_accounts,
        });

        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: globalText?.FETCHED_SUCCESSFULLY,
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
        setIsError(false);
      } else {
        // fallback to defaults
        setGraphData(DEFAULT_GRAPH_DATA);
        setFetchPageEnabled(
          response?.payload?.status !== errorcode?.errorNotFound,
        );
        toastShownRef.current = false;

        setIsError(true);
      }
    },
    [dispatch, selectedAccountTypeOption, selectedDays],
  );

  /* Fetch and update state with API data */
  const fetchData = async () => {
    if (!selectedAccountTypeOption) return;

    // Get API account type from mapping
    const accountType = ACCOUNT_TYPE_MAPPING[selectedAccountTypeOption];

    // Stop if account type is invalid
    if (!accountType) return;

    const response = await dispatch(
      AccountsPerDayThunk({
        account_type: accountType,
        period: selectedDays,
        entity_id: entityId,
      }),
    );

    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /* Button handler */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /*Auto-fetch on dependency change */
  /**
   * Effect: Trigger data fetch when autoFetch is enabled or fetch page is disabled.
   * Re-runs when autoFetch, fetchPageEnabled, or selectedDays changes.
   */
  useEffect(() => {
    if (!fetchPageEnabled || autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchPageEnabled, selectedDays, selectedAccountTypeOption]);

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${flow === homeDashboard ? "rounded-b-border-radius-md" : "rounded-border-radius-md"}`}
    >
      <GraphHeader
        handlingOptionsRequired={[
          {
            type: `${globalText?.DAY.toLowerCase()}s`,
            setterState: setSelectedDays,
          },
          {
            type: accountTypeOption,
            setterState: setSelectedAccountTypeOption,
          },
        ]}
        titleText={globalText?.ACCOUNTS}
        flow={flow}
        autoFetch={autoFetch}
        hideTitle={flow === homeDashboard ? true : false}
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
      />
      <div className="flex flex-col Qsmd:flex-row relative overflow-hidden">
        <FecthingPage
          errorData={
            AccountsPerDayError &&
            AccountsPerDayError === errorcode?.errorNotFound
              ? { status: errorcode?.errorNotFound }
              : null
          }
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={AccountsPerDayLoading || fetchPageLoading}
          isError={isError}
          handleFunction={handleFetchClick}
        />

        {/* Left column: wrapper has no intrinsic height on desktop, so the graph side decides it */}
        <div className="w-width-full flex Qsmd:flex-row flex-col">
          <div className="order-2 Qsmd:order-none Qsmd:relative Qsmd:max-w-column-width-md Qsmd:w-width-sm w-full flex-col flex-shrink-0 bg-primary-nav Qsmd:rounded-b-none Qsmd:rounded-bl-border-radius-md">
            {/* Real content: fills the wrapper on desktop, normal flow on mobile */}
            <div className="w-width-full h-auto flex flex-col items-center Qsmd:absolute Qsmd:inset-0 Qsmd:h-full flex-1 min-h-0 ">
              <div className="w-width-full flex flex-col flex-1 min-h-0">
                <div className="text-text-secondary Qsmd:text-font-xxs text-font-sm font-semibold pb-padding-md px-padding-md pt-padding-sm">
                  Accounts per day
                </div>
                <div className="flex flex-col gap-gap-sm w-width-full overflow-y-auto Qsmd:flex-1 min-h-[var(--statistics-left-container-height)] max-h-[var(--statistics-left-container-height)] Qsmd:overflow-y-auto px-padding-md overflow-x-hidden">
                  {graphData?.dataFirst?.map((val, index) => (
                    <div
                      className="flex justify-between"
                      key={graphData?.xAxisData?.[index]}
                    >
                      <div className="text-text-secondary font-normal Qsmd:text-font-xxs text-font-xs">
                        {/* {graphData.xAxisData[index]} */}
                        {dayjs(graphData.xAxisData[index]).format(
                          globalText?.DATE_FORMAT,
                        )}
                      </div>
                      <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                        {fetchPageEnabled ? "-" : val}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Border */}
                <div className=" border-b border-solid border-primary-bg mx-margin-md mt-margin-md"></div>
              </div>

              {/* Day-wise Total Row */}
              <div className="w-width-full h-auto flex justify-between mt-margin-sm pb-padding-sm px-padding-md Qsmd:my-margin-base Qsmd:pt-padding-md Qsmd:pb-padding-lg flex-shrink-0">
                <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs ">
                  Total
                </div>
                <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                  {fetchPageEnabled ? "-" : graphData.totalAccounts}
                </div>
              </div>
            </div>
          </div>

          {/* Line Graph Container with horizontal scrolling for many data points */}
          {/* Mobile: "contents" makes the graph and legend direct children of the outer flex so they can be ordered. Desktop: normal block. */}
          <div className="w-full overflow-x-hidden contents Qsmd:block">
            <div className="order-1 Qsmd:order-none w-width-full pb-padding-sm relative overflow-x-auto ">
              {/* Dynamic minimum width based on data points count for proper spacing */}
              <div
                style={{
                  minWidth: `${
                    Math.max(
                      graphData.xAxisData?.length ||
                        GlobalConfiguration?.lengthCheckZero ||
                        0,
                      GlobalConfiguration?.lengthCheckZero || 0,
                    ) *
                    SettingsStatisticsConfiguration?.graphMinWidthPerDataPoint
                  }px`,
                }}
              >
                <CommonLineGraph
                  graphDataFirst={graphData.dataFirst}
                  xAxisLabels={graphData.xAxisData}
                  graphDataFirstColor="var(--settled)"
                  defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
                  amountFormatRequiredForLegend={false}
                />
              </div>
            </div>
            {/* Footer with Accoutns per card */}

            <div className="order-3 Qsmd:order-none w-width-full bg-primary-nav rounded-b-border-radius-md Qsmd:rounded-b-none Qsmd:rounded-br-border-radius-md">
              <div className="flex Qsmd:flex-row flex-col gap-gap-sm py-padding-sm  mx-margin-md Qsmd:border-t-0 border-t-border border-primary-bg">
                <div className="flex items-center Qsmd:flex-1 gap-gap-xs ">
                  <div className="text-text-secondary text-font-xxs">
                    <div className="flex items-center">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        className="flex-shrink-0 pointer-events-none"
                      >
                        <circle
                          cx={GlobalConfiguration?.lengthCheckSeven}
                          cy={GlobalConfiguration?.lengthCheckSeven}
                          r={
                            SettingsStatisticsConfiguration?.outerStrokeRingRadius
                          }
                          fill="none"
                          stroke="var(--settled)"
                          strokeWidth={
                            SettingsStatisticsConfiguration?.outerStrokeWidth
                          }
                          style={{ pointerEvents: "none" }}
                        />
                        <circle
                          cx={GlobalConfiguration?.lengthCheckSeven}
                          cy={GlobalConfiguration?.lengthCheckSeven}
                          r={
                            SettingsStatisticsConfiguration?.innerSolidCircleRadius
                          }
                          fill="var(--settled)"
                          stroke="none"
                          style={{ pointerEvents: "none" }}
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-normal">
                    : Accounts per day
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountsPerDay;
